import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  SubaccountTransferControllerService,
  SubAccountTransferRequest,
  TransferDto,
  TransfersControllerService,
} from '@api/client';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStoreFeature,
  type,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { findOutgoingLeg, OUTCOME_CHECK_DELAY_MS } from './sub-account-transfers.utils';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { delay, exhaustMap, map, pipe, switchMap } from 'rxjs';

export type MoveFundsStatus = 'idle' | 'pending' | 'success' | 'error';

export const useSubAccountTransfers = () =>
  signalStoreFeature(
    {
      props: {
        _injector: type<Injector>(),
        _dialogService: type<MatDialog>(),
        _notificationService: type<NotificationService>(),
      },
      methods: {
        // Provided by useBalances — must be composed before this feature.
        loadBalance: type<() => void>(),
      },
    },
    withState({
      moveFundsStatus: 'idle' as MoveFundsStatus,
    }),
    withProps(() => ({
      _subAccountTransferService: inject(SubaccountTransferControllerService),
      _transfersService: inject(TransfersControllerService),
    })),
    withMethods(store => {
      const moveFunds = rxMethod<SubAccountTransferRequest>(
        pipe(
          exhaustMap(request => {
            patchState(store, { moveFundsStatus: 'pending' });

            const submittedAt = Date.now();

            return store._subAccountTransferService.transfer(request).pipe(
              delay(OUTCOME_CHECK_DELAY_MS),
              switchMap(() =>
                store._transfersService.getTransfers(request.fromAccountId),
              ),
              map(transfers => findOutgoingLeg(transfers, request, submittedAt)),
              tapResponse({
                next: (transfer: TransferDto | undefined) => {
                  if (transfer?.status === 'ERROR') {
                    patchState(store, { moveFundsStatus: 'error' });
                    store._notificationService.showErrorNotification(
                      'NOTIFICATION.FUNDS_TRANSFER_FAILED',
                    );
                  } else {
                    patchState(store, { moveFundsStatus: 'success' });
                    store._notificationService.showSuccessNotification(
                      transfer?.status === 'SETTLED'
                        ? 'NOTIFICATION.FUNDS_MOVED'
                        : 'NOTIFICATION.FUNDS_TRANSFER_SUBMITTED',
                    );
                  }
                  store.loadBalance();
                },
                error: (error: HttpErrorResponse) => {
                  patchState(store, { moveFundsStatus: 'error' });
                  store._notificationService.showErrorNotification(
                    'NOTIFICATION.FUNDS_COULDNT_BE_MOVED',
                    error,
                  );
                },
              }),
            );
          }),
        ),
      );

      const openMoveFundsDialog = async () => {
        const { MoveFundsDialogComponent } =
          await import('app/dashboard/components/move-funds-dialog/move-funds-dialog.component');

        patchState(store, { moveFundsStatus: 'idle' });

        store._dialogService.open(MoveFundsDialogComponent, {
          disableClose: true,
          injector: store._injector,
        });
      };

      return { moveFunds, openMoveFundsDialog };
    }),
  );
